/**
 * @module app.shared
 */

import { bindingMode, TaskQueue, autoinject, DOM, processContent, computedFrom } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { updateMaterializeInputLabel } from 'app/shared/lib/materialize/dom-utilities';
import { guid } from 'app/shared/lib/guid';
import { moveRulesToHelperSlot } from 'app/shared/elements/ess-rule';
import { IInput } from 'app/shared/elements/forms/input-base';
import { I18N } from 'aurelia-i18n';

@autoinject()
@processContent(moveRulesToHelperSlot)
export class EssText implements IInput {

    protected id: guid = guid();

    @bindable.stringNull({ defaultBindingMode: bindingMode.twoWay })
    public value: string | null = '';

    @bindable.string()
    public testName: string = '';

    @bindable.string()
    public type: string = "text";

    @bindable.string()
    public live: "off" | "assertive" | "polite" = "off";

    @bindable.stringNull()
    public label: string | null = null;

    @bindable.stringNull()
    public arialabelValue: string | null = null;

    @bindable.stringNull()
    public requiredValidatorLabel: string | null = null;

    @bindable.stringNull()
    public placeholder: string | null = null;

    @bindable.string()
    public autocomplete: string = 'off';

    @bindable.boolean()
    public required: boolean = false;

    @bindable.boolean()
    public allowWhitespace: boolean = true;

    @bindable.boolean()
    public readonly: boolean = false;

    @bindable.boolean()
    public disabled: boolean = false;

    @bindable.boolean()
    public clearable: boolean = false;

    @bindable.boolean()
    public showCharacterCount: boolean | null = null;

    @bindable.numberNull()
    public maxlength: number | null = null;

    @bindable.string()
    protected icon: string = '';

    @bindable.string()
    protected labelIconTooltip: string | null = null;

    @bindable.string()
    protected accesskey: string = '';

    @bindable.stringNull()
    protected labelledbyId: string | null = null;

    @bindable.stringNull()
    protected describedbyId: string | null = null;

    // refs
    public inputElement!: HTMLInputElement;
    public labelElement!: HTMLLabelElement;

    constructor(private readonly i18n: I18N,
        private readonly element: Element,
        private readonly taskQueue: TaskQueue) {
        // add focus method to custom element
        (this.element as any).focus = () => this.inputElement.focus();
    }

    protected attached(): void {
        this.placeholderChanged();
    }

    @computedFrom('label', 'requiredValidatorLabel')
    protected get requiredValueMessage() {
        return this.requiredValidatorLabel || this.i18n.tr('ui:validation:required', { what: this.i18n.tr(this.label || 'ui:labels.value') });
    }

    protected valueChanged(newValue: string | null, oldValue: string | null): void {
        if(this.inputElement === document.activeElement) {
            return;
        }
        this.queueUpdateLabel();

        if(this.element) {
            var event = new CustomEvent('value-changed', {
                detail: newValue,
                bubbles: true
            });

            this.element.dispatchEvent(event);
        }
    }

    protected clearInput(event: Event): void {
        event.stopPropagation();
        this.value = '';
    }

    protected placeholderChanged(): void {
        if(!this.placeholder) {
            this.inputElement.removeAttribute('placeholder');
        }
        this.queueUpdateLabel();
    }

    protected blur(): void {
        // forward blur event for validation
        const event = DOM.createCustomEvent('blur');
        this.element.dispatchEvent(event);
    }

    private queueUpdateLabel(): void {
        this.taskQueue.queueTask(() => {
            updateMaterializeInputLabel(this.inputElement, this.labelElement);
        });
    }
}
